import Modal from '@/components/base/Modal';
import Button from '@/components/base/Button';
import { formatMoney } from '@/utils/format';
import type { BulkTransfer } from '@/types/portal';

interface CancelBatchModalProps {
  transfer: BulkTransfer | null;
  onClose: () => void;
  onConfirm: (transfer: BulkTransfer) => void;
}

/**
 * Confirms pulling a scheduled batch before it goes out. Cancelling releases
 * nothing — the funds stay in the source wallet and no creator is paid.
 */
export default function CancelBatchModal({
  transfer,
  onClose,
  onConfirm,
}: CancelBatchModalProps) {
  return (
    <Modal
      open={!!transfer}
      onClose={onClose}
      icon="ri-close-circle-line"
      title="Cancel scheduled batch"
      subtitle={transfer ? transfer.reference : undefined}
      widthClassName="max-w-md"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Keep scheduled
          </Button>
          <Button
            icon="ri-close-circle-line"
            onClick={() => transfer && onConfirm(transfer)}
          >
            Cancel batch
          </Button>
        </>
      }
    >
      {transfer ? (
        <div className="space-y-3">
          <p className="text-sm leading-relaxed text-foreground-700">
            This batch is set to go out on{' '}
            <span className="font-medium text-foreground-950">{transfer.scheduledFor ?? 'a scheduled time'}</span>.
            Cancelling it stops the release entirely.
          </p>

          <div className="grid grid-cols-2 gap-3 rounded-md border border-background-200 bg-background-100 px-4 py-3">
            <div>
              <p className="text-[11px] font-label uppercase tracking-wide text-foreground-500">
                Recipients
              </p>
              <p className="mt-0.5 text-sm font-medium text-foreground-950">
                {transfer.recipientCount}
              </p>
            </div>
            <div>
              <p className="text-[11px] font-label uppercase tracking-wide text-foreground-500">
                Batch value
              </p>
              <p className="mt-0.5 text-sm font-medium text-foreground-950">
                {formatMoney(transfer.total, transfer.currency)}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-2.5 rounded-md border border-primary-200 bg-primary-50 px-3.5 py-3">
            <span className="mt-0.5 flex h-4 w-4 items-center justify-center text-base text-primary-600">
              <i className="ri-information-line" />
            </span>
            <p className="text-xs leading-relaxed text-primary-700">
              The funds stay in {transfer.sourceWalletName} — no creator is paid and nothing is
              debited. You can build and schedule a fresh batch at any time.
            </p>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}